/**
 * Right-side slide-in drawer — the one primitive every right-side overlay
 * drawer renders through (SA-814). Built on Radix Dialog so focus trap,
 * Esc-to-close, backdrop dismiss and aria wiring come for free; only the
 * placement/animation differ from a modal.
 *
 * SA-813: a drawer may opt in to resizing from its left edge (`resize`, wired
 * by `hooks/useDrawerWidth`); without it a drawer renders exactly as before.
 */

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/utils'
import { PanelResizeHandle } from './PanelResizeHandle'

function Drawer(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="drawer" {...props} />
}

/** A resizable drawer's width and limits, kept by the caller (`hooks/useDrawerWidth`). */
export interface DrawerResize {
  /** The stored width, else the default, inside the limits. */
  width: number
  min: number
  max: number
  /** The handle's accessible name: "Resize revisions panel". */
  label: string
  /** Store this width. */
  onCommit: (width: number) => void
  /** Forget the stored width: back to the default. */
  onReset: () => void
}

function DrawerContent({
  className,
  children,
  resize,
  overlay = true,
  style,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  resize?: DrawerResize
  /** The dimming backdrop; off for a drawer that sits beside the page (`Drawer modal={false}`). */
  overlay?: boolean
}) {
  // The width mid-drag; the caller stores only where a drag ends.
  const [liveWidth, setLiveWidth] = React.useState<number | null>(null)
  const width = resize ? liveWidth ?? resize.width : undefined
  return (
    <DialogPrimitive.Portal>
      {overlay && (
        <DialogPrimitive.Overlay
          data-slot="drawer-overlay"
          className="fixed inset-0 z-50 bg-black/50 animate-fade-in"
        />
      )}
      <DialogPrimitive.Content
        data-slot="drawer-content"
        aria-describedby={undefined}
        className={cn(
          'fixed right-0 top-0 z-50 flex h-full w-full flex-col',
          'border-l border-border bg-background shadow-2xl',
          'data-[state=open]:animate-drawer-in',
          // The handle's 1px line is the left border. The width is an inline
          // style, so a phone's full width needs the important variant.
          resize && 'max-w-full border-l-0 max-sm:w-full!',
          className,
        )}
        style={resize ? { ...style, width } : style}
        {...props}
      >
        {children}
        {/* After the content, so the drawer's first focus stays on its own
            controls; below `sm` the drawer is full width and has no edge to drag. */}
        {resize && (
          <PanelResizeHandle width={liveWidth ?? resize.width} min={resize.min} max={resize.max} label={resize.label}
            onLive={setLiveWidth} onCommit={resize.onCommit} onReset={resize.onReset} className="max-sm:hidden" hint="title" />
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

function DrawerHeader({ className, children, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-header"
      className={cn(
        'flex items-center justify-between gap-2 border-b border-border px-5 py-4',
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close
        aria-label="Close"
        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </DialogPrimitive.Close>
    </div>
  )
}

function DrawerTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="drawer-title"
      className={cn('text-base font-semibold text-foreground', className)}
      {...props}
    />
  )
}

function DrawerBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-body"
      className={cn('flex-1 space-y-5 overflow-y-auto px-5 py-4', className)}
      {...props}
    />
  )
}

function DrawerFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn(
        'flex justify-end gap-2 border-t border-border px-5 py-4',
        className,
      )}
      {...props}
    />
  )
}

export { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter }
